// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

// ** Child Imports
import Loader from '../../common/loader-components/Loader'
import { WidgetType } from 'src/lib/types/dashboardAnalytics'
import { Button, Grid, Typography } from '@mui/material'
import { appliedDateFilter, arrayToCsv, downloadCsvHelper, numberWithCommas } from 'src/lib/utils/helpers'
import { funnelEvents } from 'src/assets/AnalyticsConfig'
import { eventNameMapper } from 'src/lib/utils/analyticsHelpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import FloDataGrid from 'src/components/common/data-grid/FloDataGrid'
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { ArrowDownRight, ArrowUpRight, Download, Minus, TrendingDown, TrendingUp } from 'react-feather'

const ConversionFunnel: React.FC<WidgetType> = ({
  value,
  isLoading,
  triggerDownloadReport,
  downloadTriggered,
  moreIsGood,
  hasComparison
}) => {
  const { analyticsFilters } = useFilter()
  const modifiedValue = funnelEvents
    ?.map((event: string, index: number) => {
      return value?.find((row: any, index: number) => row?.eventName === event)
    })
    .filter((value: any) => typeof value !== 'undefined')

  useEffect(() => {
    if (downloadTriggered) {
      downloadCsvHelper(arrayToCsv(value, ['Event', 'Sessions', 'Percentage']), 'conversion_funnel_report.csv')
      typeof triggerDownloadReport !== 'undefined' && triggerDownloadReport(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTriggered])

  const columns: GridColumns = [
    {
      field: 'eventName',
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['eventName']
        const sececondayValue = row['sessions']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{ color: 'text.primary', lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {eventNameMapper(value)}
            </Typography>
            <Typography variant='body2' sx={{ color: '#949494', lineHeight: '18px' }}>
              {numberWithCommas(sececondayValue, 0)} sessions
            </Typography>
          </div>
        )
      }
    },
    {
      field: 'percentage',
      flex: 0.7,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['percentage']
        return (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'start',
              margin: 0,
              fontWeight: 500,
              color: '#4D4D4D'
            }}
          >
            <span>{numberWithCommas(value, 2)}%</span>
          </p>
        )
      }
    },
    {
      field: 'compareToVal',
      maxWidth: hasComparison ? 70 : 0,
      width: hasComparison ? 70 : 0,
      minWidth: hasComparison ? 70 : 0,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['compareToVal']
        const isGood = moreIsGood ? Boolean(parseInt(value) > 0) : Boolean(parseInt(value) < 0)

        return hasComparison ? (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              margin: 0,
              gap: 2,
              fontWeight: 600,
              color: isGood ? '#2C874A' : '#CB2711'
            }}
          >
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              isGood ? (
                <ArrowUpRight className='h-4 w-4 stroke-[2.5px]' />
              ) : (
                <ArrowDownRight className='h-4 w-4 stroke-[2.5px]' />
              )
            ) : (
              <></>
            )}
            {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
              <> {Math.sign(parseInt(value)) === -1 ? value?.slice(1) : value}</>
            ) : (
              <Minus className='text-[#949494] w-[14px] stroke-[2.5px]' />
            )}
          </p>
        ) : (
          <></>
        )
      }
    }
  ]

  return (
    <div className='relative w-full'>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          width: '100%',
          justifyContent: 'end',
          position: 'absolute',
          top: -50,
          right: 0
        }}
      >
        <Button
          variant='outlined'
          sx={{ height: 32, fontSize: '12px', color: '#6e6e6e' }}
          size='small'
          disabled={!value?.length}
          onClick={() =>
            downloadCsvHelper(arrayToCsv(value, ['Event', 'Sessions', 'Percentage']), 'conversion_funnel_report.csv')
          }
        >
          <Download className='w-4 h-4' />
        </Button>
      </div>
      <FloDataGrid
        rowHeight={48}
        totalRowCount={5}
        pagination={false}
        rows={modifiedValue ?? []}
        loading={isLoading}
        columns={columns}
        hideBorder
        hideRowDivider
        variant={'table'}
        hideHeader
        hideFooter
        removeCellPadding
        noDataJXSElement={
          <Typography variant='subtitle2' sx={{ mt: 2 }}>
            <span style={{ fontWeight: 500, color: 'text.header' }}>No data available.</span>
          </Typography>
        }
      />
    </div>
  )
}

export default ConversionFunnel
